import React from 'react';
import { Stack } from '@moda/om';
import { LineItem } from '../../hooks/useCart/cart.rest';
import { useLineItemMutations, Mode } from './useLineItemMutations';
import { LineItemActions } from './LineItemActions';
import { LineItemAlerts } from './LineItemAlerts';
import { LineItemAvailabilityNotes } from './LineItemAvailabilityNotes';
import { LineItemCustomization } from './LineItemCustomization';
import { SavedLineItemSoldOut } from './SavedLineItemSoldOut';
import { LineItemMovedAutomaticallyMessage } from './LineItemMovedAutomaticallyMessage';
import { LineItemDeliveryNotes } from './LineItemDeliveryNotes';
import { LineItemImage } from './LineItemImage';
import { LineItemOverlay } from './LineItemOverlay';
import { LineItemPrice } from './LineItemPrice';
import { LineItemProductDetails } from './LineItemProductDetails';
import { LineItemQuantitySelector } from './LineItemQuantitySelector';
import { LineItemShippingDetails } from './LineItemShippingDetails';
import { LineItemSizeSelector } from './LineItemSizeSelector';
import { LineItemPromo } from './LineItemPromo';
import { LineItemFullCell as Cell } from './LineItemFullCell';
import { LineItemBackInStock } from './LineItemBackInStock';

import './LineItemFull.scss';

type Props = {
  index: number;
  theme?: 'box' | 'simple';
  item: LineItem;
  showPriceWithVAT?: boolean;
  readOnly?: boolean;
};

const HEADER_ROW_COUNT = 3;
const FULL_ITEM_ROW_COUNT = 4;
const COLUMN_COUNT = 5;

export const LineItemFull: React.FC<Props> = ({
  index,
  theme = 'box',
  item,
  showPriceWithVAT,
  readOnly
}) => {
  const {
    handleDelete,
    handleChangeSize,
    handleChangeQuantity,
    handleChangeType,
    handleClearMovedAutomaticallyFlag,
    mode
  } = useLineItemMutations(item);

  const itemRowCount = readOnly ? FULL_ITEM_ROW_COUNT - 1 : FULL_ITEM_ROW_COUNT;
  const headerRowCount = theme === 'box' ? HEADER_ROW_COUNT : 1;
  const startRow = headerRowCount + index * itemRowCount + 1;

  return (
    <>
      <Cell
        row={startRow}
        column={1}
        columnSpan={COLUMN_COUNT}
        className="LineItemFull__availability-notes"
      >
        <LineItemAvailabilityNotes item={item} onChangeType={handleChangeType} />
      </Cell>

      <Cell
        row={startRow + 1}
        column={1}
        className={`LineItemFull__image line-item-${item.attributes.variantId}`}
      >
        <LineItemImage item={item} />
      </Cell>

      <Cell row={startRow + 1} column={2}>
        <Stack space={3}>
          <LineItemProductDetails item={item} />
          <LineItemCustomization item={item} />
          <LineItemShippingDetails item={item} />
          <SavedLineItemSoldOut item={item} />
          <LineItemBackInStock item={item} />
          <LineItemPromo item={item} />
        </Stack>
      </Cell>

      <Cell row={startRow + 1} column={3}>
        <LineItemSizeSelector item={item} readOnly={readOnly} onChange={handleChangeSize} />
      </Cell>
      <Cell row={startRow + 1} column={4}>
        <LineItemQuantitySelector item={item} readOnly={readOnly} onChange={handleChangeQuantity} />
      </Cell>
      <Cell row={startRow + 1} column={5} className="LineItemFull__price">
        <LineItemPrice
          showPriceWithVAT={showPriceWithVAT}
          direction="vertical"
          alignItems="flex-end"
          item={item}
        />
      </Cell>
      <Cell row={startRow + 1} column={3} columnSpan={3} className="LineItemFull__alerts">
        <LineItemAlerts item={item} />
      </Cell>

      <Cell
        row={startRow + 2}
        column={1}
        columnSpan={COLUMN_COUNT}
        className="LineItemFull__delivery-notes"
      >
        <Stack space={3}>
          <LineItemDeliveryNotes item={item} />
          <LineItemMovedAutomaticallyMessage
            item={item}
            onClearMovedAutomaticallyFlag={handleClearMovedAutomaticallyFlag}
          />
        </Stack>
      </Cell>

      {!readOnly && (
        <Cell
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          row={startRow + 3}
          column={1}
          columnSpan={COLUMN_COUNT}
          className="LineItemFull__actions"
        >
          <LineItemActions
            item={item}
            index={index}
            onDelete={handleDelete}
            onChangeType={handleChangeType}
          />
        </Cell>
      )}

      {mode !== Mode.Resting && (
        <Cell
          row={startRow}
          rowSpan={itemRowCount}
          column={1}
          columnSpan={COLUMN_COUNT}
          className="LineItemFull__overlay"
        >
          <LineItemOverlay item={item} mode={mode} show />
        </Cell>
      )}
    </>
  );
};
